import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listChatSessions, expandChatSession, listTenants, getChatQuality } from "../lib/api";
import Pager, { PAGE_SIZE } from "./Pager";

/* ═══ 對話品質台 V1（2026-10-11 審計提案 §三落實，取代舊「對話日誌」讀取器）═══
 * 重定位：由「原始記錄查詢器」→「平台品質監控台」——Admin 由「搵數據」變「看健康＋取證」。
 * 三欄式（清單＋氣泡閱讀器＋品質側欄）；預設自動載入近 2 日；意圖快捷篩選四枚
 * （❌答錯／轉真人／未回／有附件）；爭議取證捷徑（按號碼跳轉）。
 * 🔴唯讀紅線：Admin 不代商戶回覆客人——本頁零發送功能（回覆留在 portal）。 */

interface SessionRow {
  tenantId: string;
  sessionId: string;
  display: string;
  msgCount: number;
  lastAt: number;
  lastUserMsg: string;
  lastRole: string;
  hasAttach: boolean;
  manual: boolean;
  wrong: boolean;
  unreplied: boolean;
}

interface MessageRow {
  role: string;
  content: string;
  created_at: number;
}

interface QualityInfo {
  tenant: { tenantId: string; botName: string; planType: string; planTier: string };
  handoffCount: number;
  wrongCount: number;
}

type IntentFilter = "" | "wrong" | "manual" | "unreplied" | "attach";

/** HKT 時間標記（portal 同款：今日 HH:mm／舊訊息 M/D HH:mm） */
function fmtHKT(ms: number): string {
  const hkt = new Date(ms).toLocaleString("en-GB", { timeZone: "Asia/Hong_Kong", hour12: false });
  const hktDay = new Date(ms).toLocaleDateString("en-GB", { timeZone: "Asia/Hong_Kong" });
  const nowDay = new Date().toLocaleDateString("en-GB", { timeZone: "Asia/Hong_Kong" });
  const parts = hkt.split(", ");
  const hm = parts[1] || hkt;
  return hktDay === nowDay ? hm : parts[0].slice(0, 5) + " " + hm;
}

const INTENT_TABS: { key: IntentFilter; label: string }[] = [
  { key: "", label: "全部" },
  { key: "wrong", label: "❌ 答錯" },
  { key: "manual", label: "🧑‍💼 真人接管" },
  { key: "unreplied", label: "⏳ 未回覆" },
  { key: "attach", label: "📎 有附件" },
];

export default function ChatLogsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(2);
  const [tenantId, setTenantId] = useState("");
  const [intent, setIntent] = useState<IntentFilter>("");
  const [q, setQ] = useState("");
  const [tenants, setTenants] = useState<{ tenant_id: string; bot_name: string | null }[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<SessionRow | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [quality, setQuality] = useState<QualityInfo | null>(null);
  const [copied, setCopied] = useState("");
  const [lookupPhone, setLookupPhone] = useState("");

  const loadSessions = useCallback(async (d: number, t: string) => {
    setLoading(true);
    setError("");
    try {
      const data = await listChatSessions(t || undefined, d);
      if (data.success) setSessions(data.sessions || []);
      else setError(data.error || "載入失敗");
    } catch {
      setError("網路錯誤");
    } finally {
      setLoading(false);
    }
  }, []);

  // 預設自動載入（進頁即拉近 2 日）＋租戶下拉數據
  useEffect(() => {
    loadSessions(days, tenantId);
  }, [days, tenantId, loadSessions]);
  useEffect(() => {
    listTenants()
      .then((d) => {
        if (d.success) setTenants((d.tenants || []).map((t: { tenant_id: string; bot_name: string | null }) => ({ tenant_id: t.tenant_id, bot_name: t.bot_name })));
      })
      .catch(() => {});
  }, []);

  const handleSelect = async (s: SessionRow) => {
    setSelected(s);
    setMsgsLoading(true);
    setMessages([]);
    setQuality(null);
    try {
      const [exp, qual] = await Promise.all([
        expandChatSession(s.tenantId, s.sessionId),
        getChatQuality(s.tenantId, s.sessionId),
      ]);
      if (exp.success) setMessages(exp.messages || []);
      if (qual.success) setQuality(qual);
    } catch {
      /* 展開失敗留空，清單仍可操作 */
    } finally {
      setMsgsLoading(false);
    }
  };

  const copyTranscript = async () => {
    if (!selected) return;
    const text = messages
      .map((m) => `[${fmtHKT(m.created_at)}] ${m.role === "user" ? "客人" : m.role === "assistant" ? "AI" : "真人"}：${m.content}`)
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(`對話 ${selected.sessionId}（${selected.tenantId}）\n\n${text}`);
      setCopied("transcript");
      setTimeout(() => setCopied(""), 1500);
    } catch {
      /* clipboard 不可用時靜默 */
    }
  };

  // 爭議取證：按號碼精確跳轉——清單內找 digit 命中的 session（選中第一個）
  const handleLookup = () => {
    const digits = lookupPhone.replace(/\D/g, "");
    if (!digits) return;
    const hit = sessions.find((s) => s.sessionId.indexOf(digits) >= 0);
    if (hit) {
      handleSelect(hit);
      setQ(digits);
    } else {
      setQ(digits);
    }
  };

  const filtered = useMemo(() => {
    let list = sessions;
    if (intent === "wrong") list = list.filter((s) => s.wrong);
    else if (intent === "manual") list = list.filter((s) => s.manual);
    else if (intent === "unreplied") list = list.filter((s) => s.unreplied);
    else if (intent === "attach") list = list.filter((s) => s.hasAttach);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const qd = q.replace(/\D/g, "");
      list = list.filter(
        (s) =>
          (s.display || "").toLowerCase().includes(needle) ||
          (s.sessionId || "").toLowerCase().includes(needle) ||
          (qd && s.sessionId.replace(/\D/g, "").includes(qd)) ||
          (s.lastUserMsg || "").toLowerCase().includes(needle)
      );
    }
    return list;
  }, [sessions, intent, q]);

  const pageSessions = useMemo(() => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filtered, page]);
  useEffect(() => setPage(0), [intent, q, days, tenantId]);

  const qualityTotal = useMemo(
    () => ({
      wrong: sessions.filter((s) => s.wrong).length,
      manual: sessions.filter((s) => s.manual).length,
      unreplied: sessions.filter((s) => s.unreplied).length,
    }),
    [sessions]
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-slate-100">💬 對話監察</h1>
        <span className="text-xs text-slate-500">看健康＋取證（唯讀）——品質訊號：❌答錯／真人接管／未回覆；回覆客人請用商戶門戶</span>
      </div>

      {/* 控制列 */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-3 mb-4 flex flex-wrap gap-2 items-center">
        <select
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm"
        >
          <option value="">全部租戶</option>
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.tenant_id}{t.bot_name ? ` · ${t.bot_name}` : ""}
            </option>
          ))}
        </select>
        {[
          { d: 1, label: "今日" },
          { d: 2, label: "近 2 日" },
          { d: 7, label: "7 日" },
          { d: 30, label: "30 日" },
        ].map(({ d, label }) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              days === d ? "bg-emerald-600/20 border-emerald-500 text-emerald-300" : "bg-slate-950/40 border-slate-700/50 text-slate-400 hover:text-slate-200"
            }`}
          >
            {label}
          </button>
        ))}
        <input
          type="text"
          placeholder="🔍 搜號碼／內容"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm w-48"
        />
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? "載入中…" : `${filtered.length} 對話`}
          {!loading && (qualityTotal.wrong > 0 || qualityTotal.unreplied > 0) && (
            <span className="ml-2 text-rose-400">
              {qualityTotal.wrong > 0 && `❌${qualityTotal.wrong}`} {qualityTotal.unreplied > 0 && `⏳未回${qualityTotal.unreplied}`}
            </span>
          )}
        </span>
      </div>

      {error && <div className="text-rose-400 mb-4">❌ {error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_280px] gap-4 items-start">
        {/* 左欄：意圖篩選＋清單 */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-3 lg:max-h-[76vh] lg:overflow-y-auto">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {INTENT_TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setIntent(key)}
                className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                  intent === key ? "bg-emerald-600/20 border-emerald-500 text-emerald-300" : "bg-slate-950/40 border-slate-700/50 text-slate-400 hover:text-slate-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {loading && <div className="text-xs text-slate-500 py-6 text-center">載入中…</div>}
            {!loading && pageSessions.length === 0 && (
              <div className="text-xs text-slate-500 py-6 text-center">此篩選下無對話</div>
            )}
            {pageSessions.map((s) => (
              <button
                key={`${s.tenantId}:${s.sessionId}`}
                onClick={() => handleSelect(s)}
                className={`w-full text-left p-2.5 rounded-lg border transition-colors ${
                  selected && selected.sessionId === s.sessionId && selected.tenantId === s.tenantId
                    ? "bg-emerald-900/30 border-emerald-600"
                    : "bg-slate-800/50 border-slate-700 hover:border-slate-500"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-slate-100 truncate max-w-[150px]">{s.display}</span>
                  {s.wrong && <span title="AI 答錯被標記" className="text-[10px] px-1 rounded bg-rose-900/50 text-rose-300">❌</span>}
                  {s.manual && <span title="真人接管中" className="text-[10px] px-1 rounded bg-sky-900/50 text-sky-300">🧑‍💼</span>}
                  {s.unreplied && <span title="客人最後訊息超過 2 小時未回" className="text-[10px] px-1 rounded bg-amber-900/50 text-amber-300">⏳</span>}
                  {s.hasAttach && <span title="含附件" className="text-[10px] px-1 rounded bg-slate-700 text-slate-300">📎</span>}
                  <span className="text-[10px] text-slate-500 ml-auto">{s.msgCount} 則</span>
                </div>
                <p className="text-xs text-slate-400 truncate">{s.lastUserMsg || "（無客人訊息）"}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{s.tenantId}</span>
                  <span className="text-[10px] text-slate-600 ml-auto">{fmtHKT(s.lastAt)}</span>
                </div>
              </button>
            ))}
          </div>
          <Pager page={page} totalPages={Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))} onChange={setPage} total={filtered.length} />
        </div>

        {/* 中欄：氣泡閱讀器（portal 範式：user 綠右／AI 灰左／真人藍左＋標頭） */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 min-h-[420px] lg:max-h-[76vh] lg:overflow-hidden flex flex-col">
          {selected ? (
            <>
              <div className="flex flex-wrap items-center gap-2 pb-3 mb-3 border-b border-slate-800">
                <span className="text-sm font-semibold text-slate-100">{selected.display}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{selected.tenantId}</span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-500 truncate max-w-[220px]">
                  {selected.sessionId}
                </span>
                <span className="text-[10px] text-slate-500 ml-auto">{messages.length} 則{msgsLoading ? " · 載入中…" : ""}</span>
              </div>
              <div className="flex-1 overflow-y-auto pr-1 space-y-2">
                {messages.map((m, i) => {
                  const isUser = m.role === "user";
                  const isHuman = m.role === "human_agent" || m.role === "system";
                  return (
                    <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                          isUser
                            ? "bg-emerald-900/50 text-emerald-50 border border-emerald-800/40 rounded-br-sm"
                            : isHuman
                              ? "bg-sky-900/30 text-sky-50 border border-sky-800/40 rounded-bl-sm"
                              : "bg-slate-800 text-slate-200 rounded-bl-sm"
                        }`}
                      >
                        {isHuman && (
                          <span className="text-[10px] font-bold text-sky-400 block mb-1 tracking-wide">【真人客服回覆】</span>
                        )}
                        {m.content}
                        <span className="block text-[10px] text-slate-500 mt-1 text-right">{fmtHKT(m.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                {msgsLoading && <div className="text-center text-xs text-slate-500 py-6">載入對話中…</div>}
                {!msgsLoading && messages.length === 0 && (
                  <div className="text-center text-xs text-slate-500 py-6">此對話無訊息記錄</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-center py-16 text-sm flex-1 flex flex-col items-center justify-center">
              <div className="text-3xl mb-3">💬</div>
              ← 撳選左側對話，展開完整來龍去脈
              <div className="text-xs text-slate-600 mt-2">badge 意義：❌ 答錯標記｜🧑‍💼 真人接管中｜⏳ 客人未回覆逾 2 小時｜📎 含附件</div>
            </div>
          )}
        </div>

        {/* 右欄：品質側欄 */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 lg:max-h-[76vh] lg:overflow-y-auto">
          {selected && quality ? (
            <>
              <h3 className="text-sm font-semibold text-slate-100 mb-3">品質卡</h3>
              <div className="space-y-2 text-xs mb-4">
                <div className="flex justify-between"><span className="text-slate-500">租戶</span><span className="text-slate-200 font-mono">{quality.tenant.tenantId}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bot 名稱</span><span className="text-slate-200">{quality.tenant.botName || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">方案</span><span className="text-slate-200">{quality.tenant.planTier || quality.tenant.planType || "—"}（{quality.tenant.planType}）</span></div>
                <div className="flex justify-between"><span className="text-slate-500">轉真人次數</span><span className={quality.handoffCount > 0 ? "text-amber-400 font-semibold" : "text-slate-300"}>{quality.handoffCount}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">❌ 答錯標記</span><span className={quality.wrongCount > 0 ? "text-rose-400 font-semibold" : "text-slate-300"}>{quality.wrongCount}</span></div>
              </div>
              <h4 className="text-xs font-semibold text-slate-400 mb-2">快捷動作</h4>
              <div className="space-y-2">
                <button
                  onClick={copyTranscript}
                  className="w-full px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                >
                  {copied === "transcript" ? "✅ 已複製全文" : "📋 複製此對話全文"}
                </button>
                <button
                  onClick={() => navigate(`/tenants/edit/${selected.tenantId}`)}
                  className="w-full px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg transition-colors text-left"
                >
                  🏢 開租戶編輯頁（{selected.tenantId}）
                </button>
                <button
                  onClick={() => { setSelected(null); setQuality(null); setMessages([]); }}
                  className="w-full px-3 py-2 text-xs bg-slate-950/40 border border-slate-700/50 hover:border-slate-500 text-slate-400 rounded-lg transition-colors text-left"
                >
                  ← 清除選擇
                </button>
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-500 py-8 text-center">選中對話後顯示品質卡（租戶資料＋轉真人／答錯計數＋快捷動作）</div>
          )}
        </div>
      </div>

      {/* 爭議取證捷徑 */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-3 mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">🔎 爭議取證：</span>
        <input
          type="text"
          placeholder="客人號碼（如 8526XXXXXXX）"
          value={lookupPhone}
          onChange={(e) => setLookupPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
          className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 text-xs w-56"
        />
        <button onClick={handleLookup} className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors">
          跳轉
        </button>
        <span className="text-[11px] text-slate-600">在當前時間範圍清單內過濾並選中命中對話；搵唔到＝試轉「30 日」再跳</span>
      </div>
    </div>
  );
}
